import React, { useState } from 'react';
import {
  Check,
  CaseSensitive,
  Eye,
  Monitor,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useTheme, ThemeMode } from '../../contexts/ThemeContext/ThemeProvider';
import { useLanguage } from '../../contexts/LanguageContext';

interface ThemeToggleProps {
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  variant?: 'button' | 'dropdown' | 'minimal';
  className?: string;
}

const themeModes: ThemeMode[] = ['light', 'dark', 'system'];
const fontSizes = ['sm', 'md', 'lg', 'xl'] as const;

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  size = 'md',
  showLabel = false,
  variant = 'button',
  className = '',
}) => {
  const { t } = useTranslation('common');
  const { isRTL } = useLanguage();
  const {
    mode,
    actualTheme,
    preferences,
    setTheme,
    toggleTheme,
    setFontSize,
    setAnimations,
    setReducedMotion,
    setHighContrast,
    resetPreferences,
  } = useTheme();
  const [showSettings, setShowSettings] = useState(false);

  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
  };
  const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

  const getThemeLabel = (themeMode: ThemeMode) => t(`theme.${themeMode}`);

  const getThemeIcon = (themeMode: ThemeMode) => {
    if (themeMode === 'light') return <Sun className={`${iconSize} text-yellow-500`} />;
    if (themeMode === 'dark') return <Moon className={`${iconSize} text-blue-400`} />;
    return (
      <Monitor
        className={`${iconSize} ${
          actualTheme === 'dark' ? 'text-purple-500' : 'text-green-500'
        }`}
      />
    );
  };

  const toggleButton = (
    <button
      type='button'
      onClick={variant === 'dropdown' ? () => setShowSettings(!showSettings) : toggleTheme}
      className={`${sizeClasses[size]} flex items-center justify-center rounded-lg bg-gray-100 transition-all duration-300 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 ${variant === 'button' ? 'group' : ''}`}
      aria-label={variant === 'dropdown' ? t('theme.settings') : t('theme.changeTo')}
    >
      {variant === 'dropdown' && showSettings ? (
        <Settings className='h-4 w-4' />
      ) : (
        <span className={variant === 'button' ? 'transition-transform duration-300 group-hover:scale-110' : ''}>
          {getThemeIcon(mode)}
        </span>
      )}
    </button>
  );

  if (variant === 'minimal') {
    return <div className={className}>{toggleButton}</div>;
  }

  if (variant === 'button') {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        {toggleButton}
        {showLabel ? (
          <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
            {getThemeLabel(mode)}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`relative inline-block ${className}`}>
      {toggleButton}

      {showSettings ? (
        <>
          <div className='fixed inset-0 z-40' onClick={() => setShowSettings(false)} />

          <div
            className={clsx(
              'absolute top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800',
              isRTL ? 'left-0' : 'right-0'
            )}
          >
            <div className='border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-600 dark:bg-gray-700'>
              <div className='flex items-center justify-between'>
                <h3 className='font-semibold text-gray-900 dark:text-white'>
                  {t('theme.settings')}
                </h3>
                <button
                  type='button'
                  onClick={() => setShowSettings(false)}
                  className='rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  aria-label={t('theme.close')}
                >
                  x
                </button>
              </div>
            </div>

            <div className='space-y-4 p-4'>
              <div>
                <label className='mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  {t('theme.mode')}
                </label>
                <div className='grid grid-cols-3 gap-2'>
                  {themeModes.map(themeMode => (
                    <button
                      type='button'
                      key={themeMode}
                      onClick={() => setTheme(themeMode)}
                      className={`rounded-lg border-2 p-3 transition-all duration-300 ${
                        mode === themeMode
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                      }`}
                    >
                      <span className='flex flex-col items-center gap-2'>
                        {getThemeIcon(themeMode)}
                        <span className='text-xs font-medium text-gray-700 dark:text-gray-300'>
                          {getThemeLabel(themeMode)}
                        </span>
                        {mode === themeMode ? <Check className='h-3 w-3 text-blue-500' /> : null}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className='mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  <CaseSensitive className='me-2 inline h-4 w-4' />
                  {t('theme.fontSize')}
                </label>
                <div className='grid grid-cols-4 gap-2'>
                  {fontSizes.map(fontSize => (
                    <button
                      type='button'
                      key={fontSize}
                      onClick={() => setFontSize(fontSize)}
                      className={`rounded-lg border p-2 transition-all duration-300 ${
                        preferences.fontSize === fontSize
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <span className='block text-center'>
                        <span
                          className={`block font-medium ${
                            fontSize === 'sm'
                              ? 'text-xs'
                              : fontSize === 'md'
                                ? 'text-sm'
                                : fontSize === 'lg'
                                  ? 'text-base'
                                  : 'text-lg'
                          }`}
                        >
                          Aa
                        </span>
                        <span className='mt-1 block text-xs text-gray-500'>
                          {t(
                            fontSize === 'sm'
                              ? 'theme.small'
                              : fontSize === 'md'
                                ? 'theme.medium'
                                : fontSize === 'lg'
                                  ? 'theme.large'
                                  : 'theme.extraLarge'
                          )}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className='space-y-3'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  <Eye className='me-2 inline h-4 w-4' />
                  {t('theme.accessibility')}
                </label>
                <PreferenceSwitch
                  label={t('theme.animations')}
                  hint={t('theme.animationsHint')}
                  checked={preferences.animations}
                  onChange={() => setAnimations(!preferences.animations)}
                  isRTL={isRTL}
                />
                <PreferenceSwitch
                  label={t('theme.reducedMotion')}
                  hint={t('theme.reducedMotionHint')}
                  checked={preferences.reducedMotion}
                  onChange={() => setReducedMotion(!preferences.reducedMotion)}
                  isRTL={isRTL}
                />
                <PreferenceSwitch
                  label={t('theme.highContrast')}
                  hint={t('theme.highContrastHint')}
                  checked={preferences.highContrast}
                  onChange={() => setHighContrast(!preferences.highContrast)}
                  isRTL={isRTL}
                />
              </div>

              <div className='border-t border-gray-200 pt-2 dark:border-gray-600'>
                <button
                  type='button'
                  onClick={() => {
                    resetPreferences();
                    setShowSettings(false);
                  }}
                  className='w-full px-4 py-2 text-sm text-gray-600 transition-colors hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                >
                  {t('theme.reset')}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

const PreferenceSwitch = ({
  label,
  hint,
  checked,
  onChange,
  isRTL,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: () => void;
  isRTL: boolean;
}) => (
  <div className='flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-700'>
    <div>
      <span className='text-sm font-medium text-gray-900 dark:text-white'>{label}</span>
      <p className='text-xs text-gray-600 dark:text-gray-400'>{hint}</p>
    </div>
    <button
      type='button'
      onClick={onChange}
      aria-label={label}
      className={`relative h-6 w-11 rounded-full transition-colors duration-300 ${
        checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={clsx(
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-300',
          checked
            ? isRTL
              ? '-translate-x-5'
              : 'translate-x-5'
            : isRTL
              ? '-translate-x-0.5'
              : 'translate-x-0.5'
        )}
      />
    </button>
  </div>
);

export default ThemeToggle;
